import { useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import axios from "axios";
import { FADE_IN, SCALE_IN } from "../lib/animations";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const PasswordGate = ({ onUnlock }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/gate`, { password });
      if (res.data.success) {
        sessionStorage.setItem("gateUnlocked", "true");
        onUnlock();
      }
    } catch {
      setError("ACCESS DENIED");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[100dvh] tactical-grid flex items-center justify-center p-4" data-testid="main-app">
      <div className="noise-overlay" />
      <motion.div
        {...SCALE_IN}
        transition={{ duration: 0.3 }}
        className="w-full max-w-sm relative"
      >
        <div className="glass-overlay p-8 relative">
          {/* Top accent */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />

          <div className="flex flex-col items-center">
            {/* Logo */}
            <img src="/logo.png" alt="Union Interactive" className="w-16 h-16 object-contain mb-5" />

            {/* Terminal text */}
            <p className="font-mono text-[10px] text-zinc-600 tracking-[0.3em] mb-1">// SYSTEM STATUS</p>
            <h1 className="font-mono text-lg font-bold text-[#FF3366] tracking-wider mb-1" data-testid="gate-title">
              SYSTEM LOCKED
            </h1>
            <p className="font-mono text-[11px] text-zinc-500 tracking-widest mb-8">AWAITING CLEARANCE</p>

            <form onSubmit={handleSubmit} className="w-full space-y-4">
              <div>
                <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em] mb-2">
                  Authorization Code
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="_ _ _ _ _ _ _ _"
                  className="input-field w-full text-center tracking-[0.3em] text-sm"
                  data-testid="gate-password-input"
                  autoFocus
                />
              </div>

              {error && (
                <motion.div
                  {...FADE_IN}
                  className="flex items-center gap-2 px-3 py-2 bg-[#FF3366]/10 border border-[#FF3366]/30"
                >
                  <AlertTriangle className="w-3 h-3 text-[#FF3366]" />
                  <span className="font-mono text-xs text-[#FF3366]">{error}</span>
                </motion.div>
              )}

              <button
                type="submit"
                disabled={loading || !password}
                className="btn-primary w-full py-2.5 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="gate-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-3 h-3 border border-black/50 border-t-black rounded-full animate-spin" />
                    VERIFYING
                  </span>
                ) : "AUTHENTICATE"}
              </button>
            </form>

            <p className="font-mono text-[9px] text-zinc-700 mt-6 tracking-wider">AUTHORIZED PERSONNEL ONLY</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordGate;
